import React, { useState } from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import * as Yup from 'yup';

import FormFreeDateField from '../../Common/FormFreeItems/FormFreeDateField';
import { CancelOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { Button, CircularProgress, FormControl, Grid } from '@mui/material';
import { exportAgreementOverview } from '../agreementApi';

type FormValues = {
  end_date: string;
};

type AgreementDataTabProps = {
  handleClose: () => void;
};

export default function AgreementDataTab({ handleClose }: AgreementDataTabProps) {
  // hooks
  const [initialValues, setInitialValues] = useState<FormValues>({
    end_date: dayjs().format('YYYY-MM-DD'),
  });
  const [loadingXlsx, setLoadingXlsx] = useState(false);
  const [exportFinished, setExportFinished] = useState(false);

  const f = useFormik({
    initialValues: initialValues,
    onSubmit: handleExport,
    validationSchema: Yup.object({
      end_date: Yup.date().required('* Obligatoriskt fält').typeError('* Ogiltigt datum'),
    }),
  });

  // functions
  async function handleExport(values: FormValues) {
    if (loadingXlsx) return;

    setExportFinished(false);
    setLoadingXlsx(true);

    exportAgreementOverview(values)
      .then(() => setExportFinished(true))
      .finally(() => setLoadingXlsx(false));
  }
  return (
    <div>
      <form onSubmit={f.handleSubmit} className="pt-2">
        <div className="w-full text-neutral-600">Välj vilka datum som ska inkluderas i exporten</div>

        <div className="flex flex-col mt-4">
          <FormFreeDateField
            label="Slutdatum"
            onlyValid={false}
            size={null}
            value={f.values.end_date ? dayjs(f.values.end_date) : null}
            maxDate={dayjs()}
            onChange={(day) => f.setFieldValue('end_date', day?.format('YYYY-MM-DD'))}
            error={f.errors.end_date}
            sx={{
              '& .MuiIconButton-root': {
                color: '#439676 !important',
              },
              '& .Mui-disabled .MuiOutlinedInput-input': {
                WebkitTextFillColor: '#43967680 !important',
              },
              '& .MuiOutlinedInput-input': {
                WebkitTextFillColor: '#439676 !important',
              },
              '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                borderColor: '#43967680 !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#439676 !important',
              },
              '& .Mui-disabled.MuiInputLabel-root': {
                color: '#43967680 !important',
              },
              '& .MuiInputLabel-root': {
                color: '#439676 !important',
              },
            }}
          />
        </div>

        <hr className="my-4 mt-10 h-0.5 border-t-0 bg-neutral-100" />

        <div className="flex justify-between mt-10 h-12">
          <div className="flex w-60">
            <Button fullWidth variant="outlined" onClick={handleClose} color="error" endIcon={<CancelOutlined />}>
              Stäng
            </Button>
          </div>

          {exportFinished && (
            <div className="flex flex-col w-100 text-xs text-menuGreen text-center">
              <div className="mt-1">
                Skapad exportfil <span className="font-bold">mailas automatiskt</span> så fort den är redo...
              </div>
              <div className="mt-2">Du kan stänga fönstret!</div>
            </div>
          )}

          <div className="flex w-60">
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingXlsx || !f.isValid}
              endIcon={loadingXlsx ? <CircularProgress size="20px" /> : <FileDownloadOutlined />}
            >
              Skapa exportfil
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
